import type { ChangePoint } from "@repo/api";
import type { DefinitionVersionHistoryEntry } from "@repo/data-store";
import { type ReactNode, useMemo } from "react";
import {
  CatalogDefinitionContext,
  type CatalogDefinitionContextValue,
  type DefinitionShell,
} from "./catalog-definition-context";
import type { ShellForm } from "./use-shell-form";

interface RootProps {
  shell: DefinitionShell;
  canEdit: boolean;
  /**
   * The kind's revision lineage. Pass it and the toolbar renders the history
   * button itself, beside the version it belongs to; omit it and there is no
   * button. Positioning is not the page's business.
   */
  versions?: readonly DefinitionVersionHistoryEntry[];
  /** The instant a past view resolved. Omitted on the live read. */
  asOf?: string;
  /** Instants the graph underneath changed. Merged into the history sheet. */
  changePoints?: readonly ChangePoint[];
  children: ReactNode;
}

/** Read mode: one document-height column so the catalog page scrolls as a whole. */
export function Root({ shell, canEdit, versions, asOf, changePoints, children }: RootProps) {
  const value = useMemo<CatalogDefinitionContextValue>(
    () => ({ state: { mode: "read", shell, canEdit, versions, asOf, changePoints }, form: null }),
    [shell, canEdit, versions, asOf, changePoints],
  );

  return (
    <CatalogDefinitionContext value={value}>
      <div className="flex min-h-full w-full flex-col">{children}</div>
    </CatalogDefinitionContext>
  );
}

interface EditRootProps {
  form: ShellForm;
  canEdit: boolean;
  /**
   * As on `Root`. A builder editing an existing definition has a lineage and
   * shows the button; a create session has nothing to show yet and omits it.
   */
  versions?: readonly DefinitionVersionHistoryEntry[];
  children: ReactNode;
}

/**
 * Edit and create — a builder session with a Save button. Create is not a
 * third mode, it is edit with empty defaults, which keeps WhenReading /
 * WhenEditing total.
 *
 * Separate from Root rather than a `mode` prop on one component because
 * `useShellForm` cannot be called conditionally: a single Root would run the
 * form hook on every read page.
 */
export function EditRoot({ form, canEdit, versions, children }: EditRootProps) {
  const value = useMemo<CatalogDefinitionContextValue>(
    () => ({ state: { mode: "edit", shell: form.shell, canEdit, versions }, form }),
    [form, canEdit, versions],
  );

  return (
    <CatalogDefinitionContext value={value}>
      <form
        className="flex min-h-full w-full flex-col"
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          void form.form.handleSubmit();
        }}
      >
        {children}
      </form>
    </CatalogDefinitionContext>
  );
}
